import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus,
  Search,
  Filter,
  Package,
  ChevronRight,
  FileText,
  CheckCircle,
} from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { PageTransition } from '../../components/animations/PageTransition';
import { BUNDLE_STATUSES } from '@yoda/shared';

interface BundleListItem {
  id: string;
  projectId: string;
  projectName: string;
  requestId: string | null;
  title: string;
  status: string;
  workItemCount: number;
  approvedItemCount: number;
  creatorName: string;
  creatorAvatar: string | null;
  createdAt: string;
}

export function BundlesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bundles, setBundles] = useState<BundleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });

  useEffect(() => {
    fetchBundles();
  }, [statusFilter, pagination.page]);

  const fetchBundles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(pagination.page));
      params.set('pageSize', '20');
      if (statusFilter) params.set('status', statusFilter);

      const response = await api.get<BundleListItem[]>(
        `/bundles?${params.toString()}`
      ) as any;
      setBundles(response.data || []);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 1,
      }));
    } catch (error) {
      console.error('Failed to fetch bundles:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBundles = bundles.filter(
    (b) =>
      b.title.toLowerCase().includes(search.toLowerCase()) ||
      b.projectName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <PageTransition>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Bundles</h1>
            <p className="text-text-secondary">
              Group related work items for complex deliverables
            </p>
          </div>
          <Link to="/bundles/new">
            <Button>
              <Plus className="w-4 h-4" />
              New Bundle
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search bundles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-bg-secondary border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setSearchParams((params) => {
                if (e.target.value) params.set('status', e.target.value);
                else params.delete('status');
                return params;
              });
            }}
            className="px-4 py-2 bg-bg-secondary border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent/50"
          >
            <option value="">All Statuses</option>
            {Object.entries(BUNDLE_STATUSES).map(([key, value]) => (
              <option key={key} value={key}>
                {value.label}
              </option>
            ))}
          </select>
        </div>

        {/* Bundle List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="bg-bg-secondary rounded-xl p-4 animate-pulse"
              >
                <div className="h-5 w-2/3 bg-bg-tertiary rounded mb-2" />
                <div className="h-4 w-1/3 bg-bg-tertiary rounded" />
              </div>
            ))}
          </div>
        ) : filteredBundles.length === 0 ? (
          <div className="bg-bg-secondary rounded-2xl p-12 text-center">
            <Filter className="w-12 h-12 mx-auto text-text-muted mb-4" />
            <h3 className="text-lg font-medium mb-2">No bundles found</h3>
            <p className="text-text-secondary mb-6">
              {search || statusFilter
                ? 'Try adjusting your filters'
                : 'Create a new bundle to get started'}
            </p>
            {!search && !statusFilter && (
              <Link to="/bundles/new">
                <Button>
                  <Plus className="w-4 h-4" />
                  Create Bundle
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredBundles.map((bundle, index) => {
              const statusInfo = BUNDLE_STATUSES[bundle.status as keyof typeof BUNDLE_STATUSES];
              const progress = bundle.workItemCount > 0
                ? Math.round((bundle.approvedItemCount / bundle.workItemCount) * 100)
                : 0;

              return (
                <motion.div
                  key={bundle.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link to={`/bundles/${bundle.id}`}>
                    <div className="bg-bg-secondary rounded-xl p-4 hover:bg-bg-tertiary transition-colors group">
                      <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-accent/10">
                          <Package className="w-5 h-5 text-accent" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium truncate">{bundle.title}</h3>
                          </div>

                          <div className="flex items-center gap-3 text-sm text-text-secondary">
                            <span>{bundle.projectName}</span>
                            <span>-</span>
                            <span className="flex items-center gap-1">
                              <FileText className="w-3.5 h-3.5" />
                              {bundle.workItemCount} items
                            </span>
                            {bundle.approvedItemCount > 0 && (
                              <>
                                <span>-</span>
                                <span className="flex items-center gap-1 text-green-500">
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  {bundle.approvedItemCount} approved
                                </span>
                              </>
                            )}
                          </div>

                          {/* Progress bar */}
                          {bundle.workItemCount > 0 && (
                            <div className="mt-2">
                              <div className="h-1.5 bg-bg-tertiary rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-green-500 transition-all"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Status & Arrow */}
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span
                            className="px-3 py-1 text-xs font-medium rounded-full"
                            style={{
                              backgroundColor: statusInfo?.bgColor,
                              color: statusInfo?.color,
                            }}
                          >
                            {statusInfo?.label || bundle.status}
                          </span>
                          <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-text transition-colors" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
            >
              Previous
            </Button>
            <span className="px-4 py-2 text-sm text-text-secondary">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page === pagination.totalPages}
              onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </PageTransition>
  );
}
